/**
 * scheduleSentence — a Rhythm's schedule, said the way a manager would say it
 * (YAG-647, spec §1/§2).
 *
 * "On weekdays, every 2 hours from 8:00 to 18:00." "On weekdays at 9:00."
 * "Every day at 8:00, 12:00 and 17:00." One pure function per fact, so the
 * pane, the folded row and the editor's preview all read the same sentence off
 * the same schedule and can never disagree about what a row does.
 *
 * Nothing here fetches, and nothing here writes. The compatibility shapes
 * (`editionSlots`, `windows`, `cron`, `on`) are READ so an existing row still
 * says something true; they are never produced.
 *
 * Copy rules apply to every string this file returns: no em-dashes, middot
 * separators, and no word this surface has retired. `__tests__/copy.test.ts`
 * sweeps the sentences through the same guard the rest of the surface's copy
 * passes.
 */
import type { Schedule, ScheduleDay } from "@yagni/shared";

/** The intervals a row may fire on. Mirrors the backend's own list. */
export const SCHEDULE_INTERVAL_MINUTES: readonly number[] = [
  30, 60, 120, 180, 240, 360, 480,
];

/** Two fixed times closer than this are refused, here and at the server. */
export const MIN_TIME_SPACING_MINUTES = 30;

/** The week, in reading order. */
export const WEEK_ORDER: readonly ScheduleDay[] = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
];

export const WEEKDAYS: readonly ScheduleDay[] = ["mon", "tue", "wed", "thu", "fri"];

const DAY_SHORT: Record<ScheduleDay, string> = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

/** The chip a weekday wears in the editor. */
export const DAY_CHIP_LABEL = DAY_SHORT;

const SLOT_WORD: Record<string, string> = {
  morning: "morning",
  midday: "midday",
  evening: "evening",
};

/** "08:00" reads as "8:00"; the leading zero is typography, not information. */
export function clockLabel(hhmm: string): string {
  const parsed = parseHhmm(hhmm);
  if (parsed === null) return hhmm;
  return `${Math.floor(parsed / 60)}:${String(parsed % 60).padStart(2, "0")}`;
}

/** "HH:MM" as minutes past midnight, or null when it is not a time at all. */
export function parseHhmm(raw: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(raw.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/** How an interval is said: "30 minutes", "hour", "2 hours". */
export function intervalLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} minutes`;
  if (minutes === 60) return "hour";
  return `${minutes / 60} hours`;
}

/** "Mon and Thu"; "8:00, 12:00 and 17:00". */
export function joinAnd(parts: string[]): string {
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

/** The days a schedule runs on, in week order. Empty means every day. */
export function scheduleDays(schedule: Schedule): ScheduleDay[] {
  const chosen = new Set(schedule.days ?? []);
  return WEEK_ORDER.filter((day) => chosen.has(day));
}

/**
 * The opening clause: "Every day", "On weekdays", "On weekends", "On Mon and
 * Thu". A row that names all seven days is saying "every day", so it reads
 * that way rather than listing the week back at the reader.
 */
export function daysClause(days: readonly ScheduleDay[]): string {
  if (days.length === 0 || days.length === 7) return "Every day";
  if (days.length === 5 && WEEKDAYS.every((day) => days.includes(day))) {
    return "On weekdays";
  }
  if (days.length === 2 && days.includes("sat") && days.includes("sun")) {
    return "On weekends";
  }
  return `On ${joinAnd(days.map((day) => DAY_SHORT[day]))}`;
}

/** A schedule this editor cannot show, said honestly rather than guessed at. */
export const SCHEDULE_CUSTOM_SENTENCE =
  "On a schedule set outside this page.";

/**
 * The whole schedule as one sentence, or null when the row carries no clock
 * at all (an event craft, or a row nobody has scheduled yet).
 */
export function scheduleSentence(schedule: Schedule | null | undefined): string | null {
  if (!schedule) return null;
  const days = daysClause(scheduleDays(schedule));

  if (schedule.every && SCHEDULE_INTERVAL_MINUTES.includes(schedule.every.minutes)) {
    const every = `every ${intervalLabel(schedule.every.minutes)}`;
    const between = schedule.every.between;
    if (between && parseHhmm(between[0]) !== null && parseHhmm(between[1]) !== null) {
      return `${days}, ${every} from ${clockLabel(between[0])} to ${clockLabel(between[1])}.`;
    }
    return `${days}, ${every}.`;
  }

  const times = (schedule.times ?? []).filter((time) => parseHhmm(time) !== null);
  if (times.length > 0) {
    const sorted = [...times].sort(
      (a, b) => (parseHhmm(a) ?? 0) - (parseHhmm(b) ?? 0),
    );
    return `${days} at ${joinAnd(sorted.map(clockLabel))}.`;
  }

  // The compatibility shape: a row still keyed to the workspace's publication
  // times. The pane knows WHICH times it fires on and not what the clock says,
  // so it says exactly that much and no more.
  const slots = (schedule.editionSlots ?? []).filter((slot) => slot in SLOT_WORD);
  if (slots.length > 0) {
    const words = joinAnd(slots.map((slot) => SLOT_WORD[slot]));
    const noun = slots.length === 1 ? "publication time" : "publication times";
    return `${days} at the ${words} ${noun}.`;
  }

  if (schedule.cron || (schedule.windows ?? []).length > 0 || (schedule.on ?? []).length > 0) {
    return SCHEDULE_CUSTOM_SENTENCE;
  }
  return null;
}

/** True while this schedule is one the sentence editor can round-trip. */
export function isEditableSchedule(schedule: Schedule | null | undefined): boolean {
  if (!schedule) return false;
  if (schedule.every && SCHEDULE_INTERVAL_MINUTES.includes(schedule.every.minutes)) {
    return true;
  }
  return (schedule.times ?? []).some((time) => parseHhmm(time) !== null);
}

/* ── wall clocks: what a reader can hold a schedule to ──────────────────── */

interface LocalParts {
  /** Days since the epoch in the named timezone, for calendar-day maths. */
  day: number;
  weekday: string;
  monthDay: string;
  time: string;
}

function localParts(at: Date, tz: string | null): LocalParts | null {
  if (Number.isNaN(at.getTime())) return null;
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz ?? undefined,
      hour12: false,
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).formatToParts(at);
  } catch {
    return null;
  }
  const find = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
  const year = Number(find("year"));
  const day = Number(find("day"));
  const monthName = find("month");
  const hour = Number(find("hour")) % 24;
  const minute = Number(find("minute"));
  const monthIndex = new Date(`${monthName} 1, 2000`).getMonth();
  if (!Number.isFinite(year) || !Number.isFinite(day) || Number.isNaN(monthIndex)) {
    return null;
  }
  return {
    day: Math.floor(Date.UTC(year, monthIndex, day) / 86_400_000),
    weekday: find("weekday"),
    monthDay: `${monthName} ${day}`,
    time: `${hour}:${String(minute).padStart(2, "0")}`,
  };
}

/**
 * "today at 14:00", "tomorrow at 9:00", "yesterday at 9:00", "Thu at 9:00",
 * "Aug 3 at 9:00" — in the rhythm's own timezone, so the words match the
 * clock the scheduler reads.
 */
export function wallClockPhrase(
  iso: string,
  tz: string | null,
  now: Date = new Date(),
): string | null {
  const at = localParts(new Date(iso), tz);
  const today = localParts(now, tz);
  if (!at || !today) return null;
  const delta = at.day - today.day;
  if (delta === 0) return `today at ${at.time}`;
  if (delta === 1) return `tomorrow at ${at.time}`;
  if (delta === -1) return `yesterday at ${at.time}`;
  if (Math.abs(delta) <= 6) return `${at.weekday} at ${at.time}`;
  return `${at.monthDay} at ${at.time}`;
}

/** "in 12 minutes" / "in about an hour" / null once the day word carries it. */
function relativePrefix(at: Date, now: Date): string | null {
  const minutes = Math.round((at.getTime() - now.getTime()) / 60_000);
  if (minutes <= 0) return "due now";
  if (minutes === 1) return "in a minute";
  if (minutes < 60) return `in ${minutes} minutes`;
  const hours = Math.round(minutes / 60);
  if (hours === 1) return "in about an hour";
  if (hours < 24) return `in about ${hours} hours`;
  return null;
}

/**
 * When this rhythm fires next, as a reader would say it: how soon, and the
 * wall clock it lands on. Null when nothing is scheduled.
 */
export function nextRunSentence(
  iso: string | null,
  tz: string | null,
  now: Date = new Date(),
): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const wall = wallClockPhrase(iso, tz, now);
  if (!wall) return null;
  const relative = relativePrefix(at, now);
  return relative ? `${relative} · ${wall}` : wall;
}
